import type { InputDescription } from "@smartsuite/automation-engine-api";
import * as R from "remeda";
import type { ActionListMerger } from ".";
import type {
  ActionCandidate,
  InputCandidate,
} from "../../../../types/candidate";

export const mergeStaticAndDynamicInputDescriptions = (
  staticInputs: InputDescription[],
  dynamicInputs: InputDescription[],
): InputDescription[] => {
  const dynamicIds = new Set(dynamicInputs.map((i) => i.inputId));
  return [
    ...staticInputs.filter((i) => !dynamicIds.has(i.inputId)),
    ...dynamicInputs,
  ];
};

export const mergeInputDescriptionList =
  (actionHash: string, descriptionList: InputDescription[]): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((action) =>
        action.hash === actionHash
          ? {
              ...action,
              inputs: getNextInputList(descriptionList, action.inputs),
            }
          : action,
      ),
    );

export const mergeInputs =
  (actionHash: string, nextInputs: InputCandidate[]): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((action) =>
        action.hash === actionHash
          ? {
              ...action,
              inputs: R.map(action.inputs, (input) => {
                const nextInput = nextInputs.find(
                  (nextInput) => nextInput.inputId === input.inputId,
                );

                if (nextInput === undefined) {
                  return input;
                }
                return {
                  ...nextInput,
                  // we have to do it as for Sting editor (with smartdoc) - input value is just cloned and splitType changes are not reflected
                  ...(nextInput?.splitType === undefined && {
                    splitType: input.splitType,
                  }),
                  ...(nextInput?.createNewValues === undefined && {
                    createNewValues: input.createNewValues,
                  }),
                };
              }),
            }
          : action,
      ),
    );

export const mergeInputList =
  (actionHash: string, nextInputList: InputCandidate[]): ActionListMerger =>
  (actions: ActionCandidate[]) =>
    R.pipe(
      actions,
      R.map((action) =>
        action.hash === actionHash
          ? {
              ...action,
              inputs: nextInputList,
            }
          : action,
      ),
    );

const getNextInputList = (
  descriptionList: InputDescription[],
  existInputList: InputCandidate[],
): InputCandidate[] => {
  const existingInputsById = new Map(
    existInputList.map((input) => [input.inputId, input]),
  );

  const orderedInputs = descriptionList.map((description) => {
    const existingInput = existingInputsById.get(description.inputId);

    if (existingInput !== undefined) {
      existingInputsById.delete(description.inputId);
      return existingInput;
    }

    return {
      inputId: description.inputId,
      valueDescription: description.value,
      value: {
        values: [],
      },
    };
  });

  return [...orderedInputs, ...existingInputsById.values()];
};
